/**
 * URL Parameter Utilities
 * Helper functions for managing URL query parameters, with specific support for UTM parameters
 */

/**
 * Standard UTM parameter keys used in marketing campaigns
 */
export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export type UtmParamKey = (typeof UTM_PARAMS)[number];

export const UTM_RESTRICTED_PATHS = ['my-account', 'basket', 'delivery', 'information', 'payment', 'receipt'] as const;

/**
 * Paths where the Embedded script block renders nothing: checkout, account and login
 */
export const EMBEDDED_SCRIPT_RESTRICTED_PATHS = [...UTM_RESTRICTED_PATHS] as const;

/**
 * Check if the first path segment, or the second one after a locale prefix, is in the given list
 * @param pathname - The pathname, optionally with query string or hash
 * @param restrictedPaths - Path segments to match
 * @returns True if the path starts with one of the restricted segments
 */
function isPathInList(pathname: string, restrictedPaths: readonly string[]): boolean {
	const segments = pathname
		.split('?')[0]
		.split('#')[0]
		.split('/')
		.filter((segment) => segment.length > 0);
	const firstSegment = segments[0];
	const secondSegment = segments[1];
	const hasLocalePrefix = /^[a-z]{2}(-[a-z]{2})?$/i.test(firstSegment ?? '');

	return restrictedPaths.includes(firstSegment) || (hasLocalePrefix && restrictedPaths.includes(secondSegment));
}

export function isUtmRestrictedPath(pathname: string): boolean {
	return isPathInList(pathname, UTM_RESTRICTED_PATHS);
}

export function isEmbeddedScriptRestrictedPath(pathname: string): boolean {
	return isPathInList(pathname, EMBEDDED_SCRIPT_RESTRICTED_PATHS);
}

/**
 * Extract UTM parameters from a URLSearchParams object
 * @param searchParams - URLSearchParams object to extract from
 * @returns Record of UTM parameters that exist in the URL
 */
export function extractUtmParams(searchParams: URLSearchParams): Record<string, string> {
	const utmParams: Record<string, string> = {};

	UTM_PARAMS.forEach((key) => {
		const value = searchParams.get(key);
		if (value) {
			utmParams[key] = value;
		}
	});

	return utmParams;
}

/**
 * Extract UTM parameters from current window location
 * @returns Record of UTM parameters from current URL
 */
export function extractUtmParamsFromWindow(): Record<string, string> {
	if (typeof window === 'undefined') {
		return {};
	}
	const searchParams = new URLSearchParams(window.location.search);
	return extractUtmParams(searchParams);
}

/**
 * Merge multiple parameter objects into a single URLSearchParams object
 * Later parameters override earlier ones if keys conflict
 * @param paramGroups - Array of parameter objects to merge
 * @returns URLSearchParams containing all merged parameters
 */
export function mergeQueryParams(...paramGroups: Array<Record<string, string> | undefined>): URLSearchParams {
	const merged = new URLSearchParams();

	paramGroups.forEach((params) => {
		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null && value !== '') {
					merged.set(key, value);
				}
			});
		}
	});

	return merged;
}

/**
 * Build a query string from parameter objects while preserving UTM parameters from current URL
 * @param includeParams - Parameters to include in the query string
 * @param preserveUtm - Whether to preserve existing UTM parameters (default: true)
 * @returns Query string without leading '?', or empty string if no params
 */
export function buildQueryString(includeParams: Record<string, string> = {}, preserveUtm: boolean = true): string {
	const utmParams = preserveUtm ? extractUtmParamsFromWindow() : {};
	const merged = mergeQueryParams(utmParams, includeParams);
	const queryString = merged.toString();

	return queryString;
}

/**
 * Build a complete URL with query parameters while preserving UTM parameters
 * @param pathname - The pathname (e.g., '/contact-us')
 * @param includeParams - Additional parameters to include
 * @param hash - Optional hash/fragment (e.g., '#section')
 * @param preserveUtm - Whether to preserve existing UTM parameters (default: true)
 * @returns Complete URL string
 */
export function buildUrlWithParams(
	pathname: string,
	includeParams: Record<string, string> = {},
	hash: string = '',
	preserveUtm: boolean = true,
): string {
	const queryString = buildQueryString(includeParams, preserveUtm);
	const queryPart = queryString ? `?${queryString}` : '';
	const hashPart = hash && !hash.startsWith('#') ? `#${hash}` : hash;

	return `${pathname}${queryPart}${hashPart}`;
}

/**
 * Check if a parameter key is a UTM parameter
 * @param key - Parameter key to check
 * @returns True if the key is a UTM parameter
 */
export function isUtmParam(key: string): boolean {
	return UTM_PARAMS.includes(key as UtmParamKey);
}

/**
 * Remove all UTM parameters from a URLSearchParams object
 * @param searchParams - URLSearchParams to modify
 * @returns New URLSearchParams without UTM parameters
 */
export function removeUtmParams(searchParams: URLSearchParams): URLSearchParams {
	const cleaned = new URLSearchParams(searchParams);

	UTM_PARAMS.forEach((key) => {
		cleaned.delete(key);
	});

	return cleaned;
}

/**
 * Get all non-UTM parameters from a URLSearchParams object
 * @param searchParams - URLSearchParams to filter
 * @returns Record of non-UTM parameters
 */
export function getNonUtmParams(searchParams: URLSearchParams): Record<string, string> {
	const params: Record<string, string> = {};

	searchParams.forEach((value, key) => {
		if (!isUtmParam(key)) {
			params[key] = value;
		}
	});

	return params;
}
